"use client";

import { motion } from "framer-motion";
import { ImageIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { CookingUtensils } from "@/components/shared/CookingUtensils";
import { FadeImage } from "@/components/ui/fade-image";
import { staggerContainerVariants, staggerItemVariants } from "@/lib/animations";
import { addDays, toIsoDate } from "@/lib/calendar/week";
import type { DashboardEntry } from "@/lib/dashboard/server";
import { cn } from "@/lib/utils";

interface ComingUpWeekProps {
  entries: DashboardEntry[];
  todayIso: string;
}

interface DaySlot {
  iso: string;
  dayLabel: string;
  dateNum: number;
  isToday: boolean;
  entries: DashboardEntry[];
}

export function ComingUpWeek({ entries, todayIso }: ComingUpWeekProps) {
  const t = useTranslations("dashboard");

  const days = useMemo(() => {
    const today = new Date(todayIso + "T00:00:00Z");

    const entryMap = new Map<string, DashboardEntry[]>();
    for (const entry of entries) {
      const list = entryMap.get(entry.cookOn) ?? [];
      list.push(entry);
      entryMap.set(entry.cookOn, list);
    }

    const dayKeys = ["daySun", "dayMon", "dayTue", "dayWed", "dayThu", "dayFri", "daySat"] as const;

    const slots: DaySlot[] = [];
    for (let i = 0; i < 7; i++) {
      const d = addDays(today, i);
      const iso = toIsoDate(d);
      slots.push({
        iso,
        dayLabel: t(dayKeys[d.getUTCDay()]),
        dateNum: d.getUTCDate(),
        isToday: iso === todayIso,
        entries: entryMap.get(iso) ?? [],
      });
    }

    return slots;
  }, [entries, todayIso, t]);

  if (days.length === 0) return null;

  return (
    <section aria-label={t("comingUp")}>
      <div className="mb-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t("comingUp")}
        </p>
      </div>

      <motion.div
        variants={staggerContainerVariants(0.06)}
        initial="hidden"
        animate="visible"
        className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-2 @xl:mx-0 @xl:overflow-visible @xl:px-0"
        role="list"
      >
        {days.map((day) => (
          <motion.div
            key={day.iso}
            variants={staggerItemVariants}
            className="min-w-[160px] flex-1 @xl:min-w-0"
            role="listitem"
          >
            <DayCard day={day} />
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

function DayCard({ day }: { day: DaySlot }) {
  const t = useTranslations("dashboard");
  const hasRecipes = day.entries.length > 0;
  const images = day.entries
    .map((e) => e.recipe)
    .filter((r) => r.image)
    .map((r) => ({ src: r.image!, alt: r.title }));
  const hasImages = images.length > 0;
  const firstRecipe = day.entries[0]?.recipe;

  return (
    <div
      className={cn(
        "flex h-full flex-col overflow-hidden rounded-lg border",
        hasRecipes ? "border-border bg-card" : "border-border bg-card/50"
      )}
    >
      <div className="flex items-center justify-between px-2.5 py-1.5">
        <span
          className={cn(
            "text-xs font-medium uppercase",
            day.isToday ? "text-primary" : hasRecipes ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {day.dayLabel}
        </span>
        <span
          className={cn(
            "text-base font-semibold",
            day.isToday ? "text-primary" : hasRecipes ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {day.dateNum}
        </span>
      </div>

      {hasRecipes ? (
        <>
          {hasImages ? (
            <ImageGrid images={images} />
          ) : (
            <div className="flex aspect-square items-center justify-center bg-muted text-muted-foreground">
              <ImageIcon className="size-12 opacity-30" strokeWidth={1.5} aria-hidden />
            </div>
          )}
          {firstRecipe && (
            <div className="px-2.5 py-1.5">
              <p className="line-clamp-1 text-sm font-medium">{firstRecipe.title}</p>
              {day.entries.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  {t("recipeCount", { count: day.entries.length - 1 })}
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          <CookingUtensils />
        </div>
      )}
    </div>
  );
}

function ImageGrid({ images }: { images: { src: string; alt: string }[] }) {
  if (images.length === 1) {
    return (
      <div className="relative aspect-square overflow-hidden">
        <FadeImage
          src={images[0].src}
          alt={images[0].alt}
          fill
          sizes="(max-width: 768px) 40vw, 160px"
          className="object-cover"
        />
      </div>
    );
  }

  return (
    <div className="grid aspect-square grid-cols-2 gap-px overflow-hidden">
      {images.slice(0, 4).map((img, i) => (
        <div key={i} className="relative overflow-hidden">
          <FadeImage
            src={img.src}
            alt={img.alt}
            fill
            sizes="(max-width: 768px) 20vw, 80px"
            className="object-cover"
          />
        </div>
      ))}
    </div>
  );
}
